import { useConstructionSite } from "~api/constructionSites.ts";
import { useAllEmployees } from "~api/employees";
import { findEntity } from "~api/helpers";
import { useTranslation } from "~contexts/I18nContext/I18nContext.tsx";
import type { TimeTracking } from "~generated";
import { TimeTrackingTaskType } from "~generated";
import { getResourceName } from "~lib/resourceHelpers.ts";

export function useTimeTrackingTitle(timeTracking?: TimeTracking | null) {
  const t = useTranslation();

  const workPackage = timeTracking?.work_package;

  const { data: constructionSite, isLoading: isLoadingConstructionSite } =
    useConstructionSite(
      workPackage?.construction_site_id as string,
      undefined,
      undefined,
      {
        enabled: !!workPackage?.construction_site_id,
      },
    );

  const { data: employees, isLoading: isLoadingEmployees } = useAllEmployees(
    undefined,
    {
      enabled: !!timeTracking?.construction_site_manager_id,
    },
  );

  const isLoading = isLoadingConstructionSite || isLoadingEmployees;

  if (isLoading) {
    return { isLoading: true, title: { primary: "", secondary: "" } };
  }

  if (!timeTracking) {
    return {
      isLoading: false,
      title: { primary: t.trackingUnknownBannerTitle, secondary: "" },
    };
  }

  const constructionSiteManager = findEntity(
    employees,
    timeTracking.construction_site_manager_id,
  );

  switch (timeTracking.task_type) {
    case TimeTrackingTaskType.WORK_PACKAGE:
      return {
        isLoading: false,
        title: {
          primary: workPackage?.name ?? t.unknownWorkPackage,
          secondary: constructionSite?.number ?? t.unknownConstructionSite,
        },
      };
    case TimeTrackingTaskType.MANAGER_ACCOUNT:
      return {
        isLoading: false,
        title: {
          primary: getResourceName(
            constructionSiteManager,
            t.unknownConstructionSiteManager,
          ),
          secondary: timeTracking.task?.description ?? "",
        },
      };
    case TimeTrackingTaskType.TRAINING:
      return {
        isLoading: false,
        title: {
          primary: t.trackingTrainingBannerTitle,
          secondary: timeTracking.training_note,
        },
      };
    default:
      return {
        isLoading: false,
        title: { primary: t.trackingUnknownBannerTitle, secondary: "" },
      };
  }
}
